import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Player, type PlayerRef } from '@remotion/player';
import { Video, computeTimeline, FPS, type MusicTrack, type Overlay, type Scene, type VideoFormat, type VideoSpec } from '@hyvay/video-engine';
import { absoluteAssets, errorText, videoService, type AttachedMedia, type VideoConfig, type VideoMessage, type VideoProjectDetail, type VideoRender } from '../../services/video.service';
import { BrandBtn, Icons, Spinner, fmtCredits, readTheme } from '../../components/studio/studioUi';
import { SEOHead } from '../../components/seo/SEOHead';
import { useToast } from '../../components/common/Toast';
import { useAuth } from '../../contexts/AuthContext';
import { FONT_MONO, FONT_SANS, FONT_SERIF } from '../landing/landingData';
import { BetaChip, FORMAT_OPTIONS, SCENE_LABEL, STATUS_LABEL, Segmented, fieldLabel, input } from './videoUi';
import { AttachedChips, MediaPicker } from './MediaPicker';
import { ScheduleDialog } from './ScheduleDialog';
import { RescheduleDialog } from './ScheduleView';
import { PostRow } from './socialUi';
import { socialService, type ScheduledPost } from '../../services/social.service';
import '../landing/landing.css';

const SUGGESTIONS = ['Buat lebih singkat, 20 detik', 'Tambahkan narasi', 'Ganti musik jadi lebih santai', 'Jadikan versi 9:16 untuk Reels'];

/** /video/:id — the video studio: chat | player + scenes | settings. */
export const VideoStudio: React.FC = () => {
    const { id = '' } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const toast = useToast();
    const { user, refreshUser } = useAuth();
    const [cfg, setCfg] = useState<VideoConfig | null>(null);
    const [video, setVideo] = useState<VideoProjectDetail | null>(null);
    const [messages, setMessages] = useState<VideoMessage[]>([]);
    const [draft, setDraft] = useState('');
    const [thinking, setThinking] = useState(false);
    const [turnStage, setTurnStage] = useState('');
    const [selected, setSelected] = useState(0);
    const [renders, setRenders] = useState<VideoRender[]>([]);
    const [media, setMedia] = useState<AttachedMedia[]>([]);
    const [picker, setPicker] = useState(false);
    const [versionsOpen, setVersionsOpen] = useState(false);
    const [scheduleOpen, setScheduleOpen] = useState(false);
    const [posts, setPosts] = useState<ScheduledPost[]>([]);
    const [moving, setMoving] = useState<ScheduledPost | null>(null);
    const playerRef = useRef<PlayerRef>(null);
    const stageRef = useRef<HTMLDivElement>(null);
    const stage = useBoxSize(stageRef, !!video);
    const chatEnd = useRef<HTMLDivElement>(null);
    const saveTimer = useRef<number>();
    const firstSent = useRef(false);

    const load = useCallback(async () => {
        const v = await videoService.get(id);
        setVideo(v);
        setMessages(v.messages);
        setRenders(v.renders);
        setMedia(v.media);
        // A turn may still be running (e.g. after a refresh): show it and keep polling.
        setThinking(!!v.turn);
        setTurnStage(v.turn?.stage ?? '');
        return v;
    }, [id]);

    const send = useCallback(async (text: string) => {
        const message = text.trim();
        if (!message || thinking) return;
        setDraft('');
        setThinking(true);
        setMessages((m) => [...m, { id: `tmp-${Date.now()}`, role: 'user', content: message, createdAt: new Date().toISOString() }]);
        try {
            const r = await videoService.chat(id, message);
            setTurnStage(r.turn.stage);
        } catch (e) {
            const msg = errorText(e);
            setMessages((m) => [...m, { id: `e-${Date.now()}`, role: 'assistant', content: `⚠️ ${msg}`, createdAt: new Date().toISOString() }]);
            setThinking(false);
        }
    }, [id, thinking]);

    // Poll the background turn; when it finishes, take spec + messages from the server.
    useEffect(() => {
        if (!thinking) return;
        const t = window.setInterval(() => {
            videoService.get(id).then((v) => {
                if (v.turn) {
                    setTurnStage(v.turn.stage);
                    return;
                }
                setVideo(v);
                setMessages(v.messages);
                setMedia(v.media);
                setThinking(false);
                setTurnStage('');
                setSelected(0);
                refreshUser().catch(() => {});
            }).catch(() => {});
        }, 2000);
        return () => window.clearInterval(t);
    }, [thinking, id, refreshUser]);

    const loadPosts = useCallback(() => {
        socialService.posts(id).then(setPosts).catch(() => {});
    }, [id]);

    useEffect(() => {
        videoService.config().then(setCfg).catch(() => {});
        loadPosts();
        load()
            .then((v) => {
                // Hand-off from the "new video" dialog: send its prompt once.
                const first = (location.state as { firstMessage?: string } | null)?.firstMessage;
                if (first && !firstSent.current && v.messages.length === 0) {
                    firstSent.current = true;
                    navigate(location.pathname, { replace: true, state: null });
                    send(first);
                }
            })
            .catch((e) => toast.error(errorText(e, 'Video tidak ditemukan.')));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);

    // Braces matter: newer Chrome returns a Promise from smooth scrollIntoView, and
    // React would call that as the effect cleanup on unmount ("n is not a function").
    useEffect(() => {
        chatEnd.current?.scrollIntoView({ behavior: 'smooth' });
    }, [messages.length, thinking]);

    // Poll while a render is in flight.
    const rendering = renders.some((r) => r.status === 'QUEUED' || r.status === 'RENDERING');
    useEffect(() => {
        if (!rendering) return;
        const t = window.setInterval(() => {
            videoService.get(id).then((v) => {
                setRenders(v.renders);
                const done = v.renders[0];
                if (done?.status === 'DONE') toast.success('Video MP4 siap diunduh.');
                if (done?.status === 'FAILED') { toast.error(`Render gagal: ${done.error || ''}`); refreshUser().catch(() => {}); }
            }).catch(() => {});
        }, 2500);
        return () => window.clearInterval(t);
    }, [rendering, id, toast, refreshUser]);

    // Poll while a post of this video is going out (or due within a minute).
    const posting = posts.some((p) => p.status === 'PUBLISHING' || (p.status === 'SCHEDULED' && new Date(p.scheduledAt).getTime() < Date.now() + 60_000));
    useEffect(() => {
        if (!posting) return;
        const t = window.setInterval(loadPosts, 5000);
        return () => window.clearInterval(t);
    }, [posting, loadPosts]);

    const postAction = async (fn: () => Promise<unknown>) => {
        try {
            await fn();
            loadPosts();
        } catch (e) {
            toast.error(errorText(e));
        }
    };

    /** Local edit + debounced save (settings panel, scene editor, format). */
    const patchSpec = useCallback((fn: (s: VideoSpec) => VideoSpec) => {
        setVideo((v) => {
            if (!v) return v;
            const spec = fn(v.spec);
            window.clearTimeout(saveTimer.current);
            saveTimer.current = window.setTimeout(() => {
                videoService.update(id, { spec }).then((r) => setVideo((cur) => (cur ? { ...cur, assets: r.assets } : cur))).catch((e) => toast.error(errorText(e)));
            }, 700);
            return { ...v, spec };
        });
    }, [id, toast]);

    /** `format` renders another aspect ratio (the 9:16 copy for TikTok / Reels). */
    const startRender = async (format?: VideoFormat) => {
        try {
            const r = await videoService.render(id, format);
            setRenders((rs) => [r, ...rs]);
            refreshUser().catch(() => {});
            toast.info('Render dimulai. Kamu bisa tetap mengedit.');
        } catch (e) {
            toast.error(errorText(e));
        }
    };

    const assets = useMemo(() => (video ? absoluteAssets(video.assets) : {}), [video]);
    const timeline = useMemo(() => (video ? computeTimeline(video.spec, assets) : null), [video, assets]);
    // Preview plays the H.264 proxy of each clip when ready (HEVC is slow/unsupported in some browsers).
    const playerSpec = useMemo(() => {
        if (!video) return null;
        const proxy = new Map(media.filter((m) => m.proxyUrl).map((m) => [m.url, m.proxyUrl!]));
        if (!proxy.size) return video.spec;
        return { ...video.spec, scenes: video.spec.scenes.map((s) => (s.type === 'video' && proxy.has(s.videoUrl) ? { ...s, videoUrl: proxy.get(s.videoUrl)! } : s)) };
    }, [video, media]);
    const theme = readTheme();

    if (!video || !timeline) {
        return <div className="hv-root" data-hv-theme={theme} style={{ height: '100vh', display: 'grid', placeItems: 'center', background: 'var(--hv-desk)' }}><Spinner /></div>;
    }
    const spec = video.spec;
    const scene = spec.scenes[Math.min(selected, spec.scenes.length - 1)];
    // Versions are numbered oldest-first (v1 = first render); the list is newest-first.
    const versionOf = (r: VideoRender) => renders.length - renders.findIndex((x) => x.id === r.id);
    const latestDone = renders.find((r) => r.status === 'DONE' && r.url);
    // Captions need something to show: narration, or a clip playing its own speech.
    const canCaption = spec.voice.enabled || spec.scenes.some((x) => x.type === 'video' && x.muted === false);
    const attach = async (assets: { assetId: string }[]) => {
        try {
            setMedia(await videoService.attach(id, assets.map((a) => a.assetId)));
            setDraft((d) => d || 'Pakai file yang baru saya lampirkan.');
        } catch (e) {
            toast.error(errorText(e));
        }
    };
    const detach = async (assetId: string) => {
        try {
            setMedia(await videoService.detach(id, assetId));
        } catch (e) {
            toast.error(errorText(e));
        }
    };
    const seek = (i: number) => {
        setSelected(i);
        playerRef.current?.seekTo(timeline.scenes[i].from + 6);
    };

    return (
        <div className="hv-root hv-st" data-hv-theme={theme} style={{ height: '100vh', padding: 14, boxSizing: 'border-box', background: 'var(--hv-desk)', fontFamily: FONT_SANS, color: 'var(--hv-ink)' }}>
            <SEOHead title={`Video - ${video.name}`} noindex={true} />
            <div style={{ height: '100%', display: 'flex', flexDirection: 'column', borderRadius: 26, overflow: 'hidden', background: 'var(--hv-page)', border: '1px solid var(--hv-line)' }}>
                {/* Header */}
                <div style={{ height: 62, flex: 'none', display: 'flex', alignItems: 'center', gap: 14, padding: '0 18px', borderBottom: '1px solid var(--hv-line)' }}>
                    <button type="button" onClick={() => navigate('/video')} title="Semua video" style={{ border: 0, background: 'transparent', cursor: 'pointer', color: 'var(--hv-muted)', fontSize: 18 }}>←</button>
                    <input
                        defaultValue={video.name}
                        onBlur={(e) => e.target.value.trim() && e.target.value !== video.name && videoService.update(id, { name: e.target.value }).catch(() => {})}
                        style={{ border: 0, background: 'transparent', fontSize: 16, fontWeight: 700, color: 'var(--hv-ink)', fontFamily: 'inherit', outline: 'none', minWidth: 120, maxWidth: 280 }}
                    />
                    <BetaChip />
                    <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 12 }}>
                        <Segmented value={spec.format} options={FORMAT_OPTIONS} onChange={(format) => patchSpec((s) => ({ ...s, format }))} />
                        <span style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, color: 'var(--hv-body)' }}><Icons.coins /> {fmtCredits(user?.credits)}</span>
                        {latestDone && (
                            <div style={{ position: 'relative', display: 'flex' }}>
                                <a href={latestDone.url!} download className="hv-h-mint-bd" style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 14px', borderRadius: '999px 0 0 999px', border: '1px solid var(--hv-line)', color: 'var(--hv-ink)', fontSize: 13.5, fontWeight: 700 }}>
                                    <Icons.download /> Unduh MP4 · v{versionOf(latestDone)}
                                </a>
                                <button type="button" onClick={() => setVersionsOpen((o) => !o)} title="Semua versi" style={{ height: 40, padding: '0 11px', borderRadius: '0 999px 999px 0', border: '1px solid var(--hv-line)', borderLeft: 0, background: 'transparent', color: 'var(--hv-ink)', cursor: 'pointer' }}>▾</button>
                                {versionsOpen && (
                                    <div style={{ position: 'absolute', top: 'calc(100% + 8px)', right: 0, zIndex: 20, width: 300, padding: 6, borderRadius: 14, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', boxShadow: '0 20px 40px -20px var(--hv-shadow)' }}>
                                        {renders.map((r) => (
                                            <div key={r.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', borderRadius: 10, fontSize: 13 }}>
                                                <b style={{ fontFamily: FONT_MONO, fontSize: 12 }}>v{versionOf(r)}</b>
                                                <span style={{ flex: 1, color: 'var(--hv-muted)' }}>{new Date(r.createdAt).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' })}{r.bytes ? ` · ${(r.bytes / 1e6).toFixed(1)} MB` : ''}</span>
                                                {r.status === 'DONE' && r.url ? (
                                                    <a href={r.url} download onClick={() => setVersionsOpen(false)} style={{ fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 5 }}><Icons.download /> Unduh</a>
                                                ) : (
                                                    <span style={{ color: r.status === 'FAILED' ? 'var(--hv-danger)' : 'var(--hv-muted)' }}>{STATUS_LABEL[r.status]}</span>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        {latestDone && (
                            <button type="button" className="hv-h-mint-bd" data-track="video_schedule_open" onClick={() => setScheduleOpen(true)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, height: 40, padding: '0 14px', borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', color: 'var(--hv-ink)', fontSize: 13.5, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit' }}>
                                <Icons.share /> Posting / Jadwalkan
                            </button>
                        )}
                        <BrandBtn data-track="video_render" onClick={() => startRender()} disabled={rendering || !cfg?.renderEnabled} title={cfg?.renderEnabled ? '' : 'Render belum tersedia di server ini'}>
                            {rendering ? <><Spinner light size={14} /> Merender…</> : <>Render MP4 · {cfg?.renderCost ?? 10} credit</>}
                        </BrandBtn>
                    </div>
                </div>

                <div style={{ flex: 1, minHeight: 0, display: 'grid', gridTemplateColumns: '360px 1fr 300px' }}>
                    {/* Chat */}
                    <div style={{ display: 'flex', flexDirection: 'column', minHeight: 0, borderRight: '1px solid var(--hv-line)' }}>
                        <div style={{ flex: 1, overflow: 'auto', padding: 18, display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {messages.length === 0 && !thinking && (
                                <div style={{ color: 'var(--hv-muted)', fontSize: 14, lineHeight: 1.6 }}>
                                    Ceritakan video yang kamu mau: usaha apa, promo apa, berapa detik, perlu narasi atau tidak.
                                </div>
                            )}
                            {messages.map((m) => (
                                <div key={m.id} style={{ alignSelf: m.role === 'user' ? 'flex-end' : 'flex-start', maxWidth: '88%', padding: '11px 14px', borderRadius: m.role === 'user' ? '16px 16px 4px 16px' : '16px 16px 16px 4px', background: m.role === 'user' ? 'var(--brand,#13422C)' : 'var(--hv-card)', color: m.role === 'user' ? '#FFFDF7' : 'var(--hv-ink)', border: m.role === 'user' ? 0 : '1px solid var(--hv-line)', fontSize: 14, lineHeight: 1.5, whiteSpace: 'pre-wrap' }}>
                                    {m.content}
                                </div>
                            ))}
                            {thinking && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '11px 14px', borderRadius: 16, background: 'var(--hv-card)', border: '1px solid var(--hv-line)', fontSize: 14, color: 'var(--hv-body)', alignSelf: 'flex-start' }}>
                                    <Spinner size={15} /> {turnStage || 'Menyusun scene…'}
                                    {media.some((m) => m.kind === 'video') && <span style={{ color: 'var(--hv-muted)', fontSize: 12.5 }}>· boleh tinggalkan halaman ini</span>}
                                </div>
                            )}
                            <div ref={chatEnd} />
                        </div>
                        {!thinking && messages.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, padding: '0 16px 10px' }}>
                                {SUGGESTIONS.map((s) => (
                                    <button key={s} type="button" className="hv-h-chip" onClick={() => send(s)} style={{ padding: '6px 11px', borderRadius: 999, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', color: 'var(--hv-body)', fontSize: 12, cursor: 'pointer', fontFamily: 'inherit' }}>{s}</button>
                                ))}
                            </div>
                        )}
                        {media.length > 0 && (
                            <div style={{ padding: '0 16px 10px' }}>
                                <div style={{ ...fieldLabel, marginBottom: 6 }}>File terlampir</div>
                                <AttachedChips items={media} onRemove={detach} />
                            </div>
                        )}
                        <form onSubmit={(e) => { e.preventDefault(); send(draft); }} style={{ margin: '0 14px 14px', display: 'flex', gap: 8, alignItems: 'flex-end', padding: 10, borderRadius: 18, border: '1px solid var(--hv-line)', background: 'var(--hv-card)' }} className="hv-composer">
                            <button type="button" onClick={() => setPicker(true)} title="Lampirkan gambar / video" style={{ width: 36, height: 36, flex: 'none', borderRadius: 999, border: '1px solid var(--hv-line)', background: 'transparent', color: 'var(--hv-body)', cursor: 'pointer', display: 'grid', placeItems: 'center' }}>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 11.5 12.5 20a5 5 0 0 1-7-7L14 4.5a3.4 3.4 0 0 1 4.8 4.8l-8.4 8.4a1.7 1.7 0 0 1-2.4-2.4l7.7-7.7" /></svg>
                            </button>
                            <textarea
                                value={draft}
                                onChange={(e) => setDraft(e.target.value)}
                                onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); send(draft); } }}
                                rows={2}
                                placeholder="Minta perubahan apa saja…"
                                style={{ flex: 1, border: 0, outline: 'none', resize: 'none', background: 'transparent', color: 'var(--hv-ink)', fontSize: 14, fontFamily: 'inherit', lineHeight: 1.5 }}
                            />
                            <button type="submit" disabled={!draft.trim() || thinking} style={{ width: 36, height: 36, borderRadius: 999, border: 0, background: draft.trim() ? 'var(--brand,#13422C)' : 'var(--hv-line)', color: '#fff', cursor: 'pointer', display: 'grid', placeItems: 'center' }}><Icons.up size={15} /></button>
                        </form>
                    </div>

                    {/* Player + scenes */}
                    <div style={{ minWidth: 0, minHeight: 0, display: 'flex', flexDirection: 'column', background: 'var(--hv-page-2)' }}>
                        <div ref={stageRef} style={{ flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 22 }}>
                            <div style={{ ...fit(stage, timeline.width / timeline.height), borderRadius: 14, overflow: 'hidden', boxShadow: '0 30px 60px -40px var(--hv-shadow)', background: '#000' }}>
                                <Player
                                    ref={playerRef}
                                    component={Video}
                                    inputProps={{ spec: playerSpec ?? spec, assets }}
                                    durationInFrames={timeline.durationInFrames}
                                    compositionWidth={timeline.width}
                                    compositionHeight={timeline.height}
                                    fps={FPS}
                                    controls
                                    loop
                                    initialFrame={45}
                                    style={{ width: '100%', height: '100%' }}
                                />
                            </div>
                        </div>
                        <div style={{ flex: 'none', display: 'flex', gap: 8, overflowX: 'auto', padding: '0 22px 18px' }}>
                            {spec.scenes.map((s, i) => (
                                <button key={s.id} type="button" onClick={() => seek(i)} style={{ flex: 'none', width: 128, textAlign: 'left', padding: '10px 12px', borderRadius: 12, cursor: 'pointer', fontFamily: 'inherit', background: 'var(--hv-card)', color: 'var(--hv-ink)', border: `2px solid ${i === selected ? 'var(--brand,#13422C)' : 'var(--hv-line)'}` }}>
                                    <div style={{ fontFamily: FONT_MONO, fontSize: 10.5, color: 'var(--hv-muted)' }}>{String(i + 1).padStart(2, '0')} · {(timeline.scenes[i].frames / FPS).toFixed(1)} dtk</div>
                                    <div style={{ marginTop: 4, fontSize: 13, fontWeight: 700 }}>{SCENE_LABEL[s.type] ?? s.type}</div>
                                    <div style={{ marginTop: 2, fontSize: 11.5, color: 'var(--hv-muted)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{sceneSummary(s)}</div>
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Settings */}
                    <div style={{ minHeight: 0, overflow: 'auto', borderLeft: '1px solid var(--hv-line)', padding: 18, display: 'flex', flexDirection: 'column', gap: 20 }}>
                        <Section title="Hasil render">
                            {renders.length === 0 && <div style={{ fontSize: 12.5, color: 'var(--hv-muted)' }}>Belum ada. Klik <b>Render MP4</b> di atas.</div>}
                            <div style={{ display: 'grid', gap: 8 }}>
                                {renders.map((r) => (
                                    <div key={r.id} style={{ padding: '10px 12px', borderRadius: 12, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', fontSize: 12.5 }}>
                                        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8 }}>
                                            <b>v{versionOf(r)}{r.format ? ` · ${FORMAT_OPTIONS.find((f) => f.value === r.format)?.label}` : ''} · {STATUS_LABEL[r.status]}</b>
                                            <span style={{ color: 'var(--hv-muted)' }}>{new Date(r.createdAt).toLocaleString('id-ID', { dateStyle: 'short', timeStyle: 'short' })}</span>
                                        </div>
                                        {(r.status === 'RENDERING' || r.status === 'QUEUED') && (
                                            <div style={{ marginTop: 8, height: 6, borderRadius: 99, background: 'var(--hv-page-2)', overflow: 'hidden' }}><div style={{ width: `${Math.round(r.progress * 100)}%`, height: '100%', background: 'var(--hv-leaf)', transition: 'width .4s' }} /></div>
                                        )}
                                        {r.status === 'DONE' && r.url && (
                                            <a href={r.url} target="_blank" rel="noreferrer" download style={{ marginTop: 8, display: 'inline-flex', alignItems: 'center', gap: 6, fontWeight: 700 }}><Icons.download /> Unduh MP4{r.bytes ? ` (${(r.bytes / 1e6).toFixed(1)} MB)` : ''}</a>
                                        )}
                                        {r.status === 'FAILED' && <div title={r.error || ''} style={{ marginTop: 6, color: 'var(--hv-danger)', overflowWrap: 'anywhere' }}>{(r.error || 'Render gagal').slice(0, 120)}{(r.error?.length ?? 0) > 120 ? '…' : ''} · credit dikembalikan</div>}
                                    </div>
                                ))}
                            </div>
                        </Section>
                        <Section title="Jadwal posting">
                            {posts.length === 0 ? (
                                <div style={{ fontSize: 12.5, color: 'var(--hv-muted)' }}>
                                    {latestDone ? <>Belum ada. Klik <b>Posting / Jadwalkan</b> untuk kirim ke YouTube, TikTok, LinkedIn, Facebook, atau Instagram.</> : 'Render dulu, lalu posting langsung ke media sosial.'}
                                </div>
                            ) : (
                                <div style={{ display: 'grid', gap: 8 }}>
                                    {posts.map((p) => (
                                        <PostRow key={p.id} post={p} onCancel={() => postAction(() => socialService.cancel(p.id))} onRetry={() => postAction(() => socialService.retry(p.id))} onReschedule={() => setMoving(p)} />
                                    ))}
                                </div>
                            )}
                            <button type="button" onClick={() => navigate('/video/schedule')} style={{ marginTop: 8, border: 0, background: 'transparent', padding: 0, cursor: 'pointer', fontSize: 12.5, fontWeight: 700, color: 'var(--hv-ink)', fontFamily: 'inherit' }}>Semua jadwal & akun →</button>
                        </Section>
                        <Section title="Musik">
                            <select value={spec.music.track} onChange={(e) => patchSpec((s) => ({ ...s, music: { ...s.music, track: e.target.value as MusicTrack } }))} style={input}>
                                <option value="upbeat">Ceria (upbeat)</option>
                                <option value="calm">Santai (calm)</option>
                                <option value="corporate">Profesional (corporate)</option>
                                <option value="none">Tanpa musik</option>
                            </select>
                            <input type="range" min={0} max={1} step={0.05} value={spec.music.volume} onChange={(e) => patchSpec((s) => ({ ...s, music: { ...s.music, volume: parseFloat(e.target.value) } }))} style={{ width: '100%', marginTop: 10 }} />
                        </Section>
                        <Section title="Narasi">
                            <label style={{ display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, cursor: cfg?.voiceEnabled ? 'pointer' : 'not-allowed', color: cfg?.voiceEnabled ? 'var(--hv-ink)' : 'var(--hv-muted)' }}>
                                <input type="checkbox" disabled={!cfg?.voiceEnabled} checked={spec.voice.enabled} onChange={(e) => patchSpec((s) => ({ ...s, voice: { enabled: e.target.checked } }))} />
                                Suara narator (Bahasa Indonesia)
                            </label>
                            {!cfg?.voiceEnabled && <div style={{ marginTop: 6, fontSize: 12, color: 'var(--hv-muted)' }}>TTS belum dikonfigurasi di server.</div>}
                            <label style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10, fontSize: 13.5, cursor: canCaption ? 'pointer' : 'not-allowed', color: canCaption ? 'var(--hv-ink)' : 'var(--hv-muted)' }}>
                                <input type="checkbox" disabled={!canCaption} checked={!!spec.captions?.enabled} onChange={(e) => patchSpec((s) => ({ ...s, captions: { enabled: e.target.checked } }))} />
                                Caption di bawah video (narasi & ucapan asli rekaman)
                            </label>
                        </Section>
                        <Section title="Warna & font">
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8 }}>
                                {(['primary', 'accent', 'background', 'ink'] as const).map((k) => (
                                    <label key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, color: 'var(--hv-body)' }}>
                                        <input type="color" value={spec.theme[k]} onChange={(e) => patchSpec((s) => ({ ...s, theme: { ...s.theme, [k]: e.target.value } }))} style={{ width: 30, height: 30, border: 0, padding: 0, background: 'transparent' }} />
                                        {{ primary: 'Utama', accent: 'Aksen', background: 'Latar', ink: 'Teks' }[k]}
                                    </label>
                                ))}
                            </div>
                            <div style={{ marginTop: 10 }}>
                                <Segmented value={spec.theme.headingFont} onChange={(headingFont) => patchSpec((s) => ({ ...s, theme: { ...s.theme, headingFont } }))} options={[{ value: 'serif', label: 'Elegan' }, { value: 'sans', label: 'Modern' }]} />
                            </div>
                        </Section>
                        {scene && (
                            <Section title={`Scene ${selected + 1} · ${SCENE_LABEL[scene.type] ?? scene.type}`}>
                                <SceneEditor scene={scene} onChange={(next) => patchSpec((s) => ({ ...s, scenes: s.scenes.map((x, i) => (i === selected ? next : x)) }))} />
                            </Section>
                        )}
                    </div>
                </div>
            </div>
            <MediaPicker open={picker} onClose={() => setPicker(false)} exclude={media.map((m) => m.assetId)} onDone={attach} />
            <ScheduleDialog
                open={scheduleOpen}
                onClose={() => setScheduleOpen(false)}
                videoName={video.name}
                renders={renders}
                renderCost={cfg?.renderCost ?? 10}
                onRenderPortrait={() => startRender('portrait')}
                onScheduled={loadPosts}
            />
            <RescheduleDialog post={moving} maxAheadDays={90} onClose={() => setMoving(null)} onSaved={loadPosts} />
        </div>
    );
};

/** Content-box size of an element, tracked live (attaches once `ready`). */
function useBoxSize(ref: React.RefObject<HTMLElement>, ready: boolean) {
    const [size, setSize] = useState({ w: 0, h: 0 });
    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        const ro = new ResizeObserver(([e]) => setSize({ w: e.contentRect.width, h: e.contentRect.height }));
        ro.observe(el);
        return () => ro.disconnect();
    }, [ref, ready]);
    return size;
}

/** Largest box of `ratio` (w/h) that fits the stage. */
function fit(stage: { w: number; h: number }, ratio: number): React.CSSProperties {
    if (!stage.w || !stage.h) return { width: 0, height: 0 };
    const w = Math.min(stage.w, stage.h * ratio);
    return { width: Math.floor(w), height: Math.floor(w / ratio) };
}

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({ title, children }) => (
    <div>
        <div style={fieldLabel}>{title}</div>
        {children}
    </div>
);

function sceneSummary(s: Scene): string {
    switch (s.type) {
        case 'title': case 'features': case 'cta': return s.title;
        case 'statement': return s.text;
        case 'stat': return `${s.value} ${s.label}`;
        case 'quote': return s.quote;
        case 'image': return s.title || s.caption || '';
        case 'screenshot': return s.caption || s.url || '';
        case 'video': return s.title || s.caption || 'Klip video';
    }
}

/** Plain-field editor for the selected scene (text, duration, narration). */
const SceneEditor: React.FC<{ scene: Scene; onChange: (s: Scene) => void }> = ({ scene, onChange }) => {
    const fields: Record<Scene['type'], [string, string][]> = {
        title: [['eyebrow', 'Label kecil'], ['title', 'Judul'], ['subtitle', 'Subjudul']],
        statement: [['text', 'Kalimat'], ['emphasis', 'Kata yang ditonjolkan']],
        screenshot: [['caption', 'Keterangan'], ['url', 'Alamat']],
        image: [['title', 'Judul'], ['caption', 'Keterangan']],
        features: [['title', 'Judul']],
        stat: [['value', 'Angka'], ['label', 'Label'], ['sub', 'Keterangan']],
        quote: [['quote', 'Kutipan'], ['author', 'Nama'], ['role', 'Peran']],
        cta: [['title', 'Judul'], ['subtitle', 'Subjudul'], ['buttonText', 'Tombol'], ['url', 'Alamat']],
        video: [['title', 'Judul'], ['caption', 'Keterangan']],
    };
    const set = (k: string, v: unknown) => onChange({ ...scene, [k]: v } as Scene);
    const rec = scene as unknown as Record<string, unknown>;
    return (
        <div style={{ display: 'grid', gap: 8 }}>
            {fields[scene.type].map(([k, label]) => (
                <label key={k} style={{ fontSize: 12, color: 'var(--hv-muted)' }}>
                    {label}
                    <input value={String(rec[k] ?? '')} onChange={(e) => set(k, e.target.value)} style={{ ...input, marginTop: 4 }} />
                </label>
            ))}
            {scene.type === 'video' && (
                <>
                    <label style={{ fontSize: 12, color: 'var(--hv-muted)' }}>
                        Mulai dari detik ke-
                        <input type="number" min={0} step={0.5} value={scene.trimStart ?? 0} onChange={(e) => set('trimStart', Math.max(0, parseFloat(e.target.value) || 0))} style={{ ...input, marginTop: 4 }} />
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: 'var(--hv-ink)' }}>
                        <input type="checkbox" checked={scene.muted === false} onChange={(e) => set('muted', !e.target.checked)} />
                        Pakai suara asli video (musik otomatis mengecil)
                    </label>
                    <OverlayEditor overlays={scene.overlays ?? []} onChange={(overlays) => set('overlays', overlays)} />
                    {scene.muted === false && !!scene.subtitles?.length && (
                        <SubtitleEditor subtitles={scene.subtitles} onChange={(subtitles) => set('subtitles', subtitles)} />
                    )}
                </>
            )}
            {scene.type === 'features' && (
                <label style={{ fontSize: 12, color: 'var(--hv-muted)' }}>
                    Poin (satu per baris)
                    <textarea value={scene.items.join('\n')} onChange={(e) => set('items', e.target.value.split('\n'))} rows={4} style={{ ...input, marginTop: 4, resize: 'vertical' }} />
                </label>
            )}
            <label style={{ fontSize: 12, color: 'var(--hv-muted)' }}>
                Durasi (detik)
                <input type="number" min={2} max={15} step={0.5} value={scene.duration} onChange={(e) => set('duration', parseFloat(e.target.value) || 2)} style={{ ...input, marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 12, color: 'var(--hv-muted)' }}>
                Narasi
                <textarea value={scene.narration ?? ''} onChange={(e) => set('narration', e.target.value)} rows={3} style={{ ...input, marginTop: 4, resize: 'vertical' }} />
            </label>
        </div>
    );
};

const OVERLAY_STYLE: { value: Overlay['style']; label: string }[] = [
    { value: 'card', label: 'Kartu' },
    { value: 'pip', label: 'Mengambang' },
    { value: 'full', label: 'Layar penuh' },
];

/** Product pictures shown over the clip: when, how long, which style, text. Position is automatic. */
const OverlayEditor: React.FC<{ overlays: Overlay[]; onChange: (o: Overlay[]) => void }> = ({ overlays, onChange }) => {
    const set = (i: number, patch: Partial<Overlay>) => onChange(overlays.map((o, j) => (j === i ? { ...o, ...patch } : o)));
    if (!overlays.length) return <div style={{ fontSize: 12, color: 'var(--hv-muted)' }}>Belum ada produk di atas video. Minta lewat chat, mis. "munculkan foto produk saat saya memegangnya".</div>;
    return (
        <div style={{ display: 'grid', gap: 8 }}>
            <div style={{ ...fieldLabel, marginBottom: 0 }}>Produk di atas video</div>
            {overlays.map((o, i) => (
                <div key={i} style={{ padding: 10, borderRadius: 12, border: '1px solid var(--hv-line)', background: 'var(--hv-card)', display: 'grid', gap: 6 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ width: 34, height: 34, borderRadius: 8, background: `center/contain no-repeat url("${o.imageUrl}"), var(--hv-page-2)` }} />
                        <select value={o.style} onChange={(e) => set(i, { style: e.target.value as Overlay['style'] })} style={{ ...input, padding: '6px 8px', flex: 1 }}>
                            {OVERLAY_STYLE.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
                        </select>
                        <button type="button" title="Hapus" onClick={() => onChange(overlays.filter((_, j) => j !== i))} style={{ border: 0, background: 'transparent', color: 'var(--hv-danger)', cursor: 'pointer', fontSize: 16 }}>×</button>
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                        <label style={{ fontSize: 11.5, color: 'var(--hv-muted)' }}>Muncul (dtk)
                            <input type="number" min={0} step={0.5} value={o.start} onChange={(e) => set(i, { start: Math.max(0, parseFloat(e.target.value) || 0) })} style={{ ...input, padding: '6px 8px', marginTop: 3 }} />
                        </label>
                        <label style={{ fontSize: 11.5, color: 'var(--hv-muted)' }}>Lama (dtk)
                            <input type="number" min={1} step={0.5} value={o.duration} onChange={(e) => set(i, { duration: Math.max(1, parseFloat(e.target.value) || 1) })} style={{ ...input, padding: '6px 8px', marginTop: 3 }} />
                        </label>
                    </div>
                    {o.style !== 'pip' && (
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
                            <input placeholder="Nama produk" value={o.title ?? ''} onChange={(e) => set(i, { title: e.target.value })} style={{ ...input, padding: '6px 8px' }} />
                            <input placeholder="Harga / promo" value={o.price ?? ''} onChange={(e) => set(i, { price: e.target.value })} style={{ ...input, padding: '6px 8px' }} />
                        </div>
                    )}
                </div>
            ))}
        </div>
    );
};

/** Correct the automatic transcript (e.g. a misheard brand name). Times stay as transcribed. */
const SubtitleEditor: React.FC<{ subtitles: { start: number; end: number; text: string }[]; onChange: (s: { start: number; end: number; text: string }[]) => void }> = ({ subtitles, onChange }) => {
    const [open, setOpen] = useState(false);
    const fmt = (t: number) => `${Math.floor(t / 60)}:${(t % 60).toFixed(1).padStart(4, '0')}`;
    return (
        <div>
            <button type="button" onClick={() => setOpen((o) => !o)} style={{ ...fieldLabel, marginBottom: 0, border: 0, background: 'transparent', padding: 0, cursor: 'pointer' }}>
                {open ? '▾' : '▸'} Caption ucapan ({subtitles.length}) · bisa dikoreksi
            </button>
            {open && (
                <div style={{ marginTop: 8, display: 'grid', gap: 6, maxHeight: 320, overflow: 'auto' }}>
                    {subtitles.map((x, i) => (
                        <label key={i} style={{ display: 'grid', gridTemplateColumns: '52px 1fr', gap: 6, alignItems: 'center', fontFamily: FONT_MONO, fontSize: 11, color: 'var(--hv-muted)' }}>
                            {fmt(x.start)}
                            <input value={x.text} onChange={(e) => onChange(subtitles.map((y, j) => (j === i ? { ...y, text: e.target.value } : y)))} style={{ ...input, padding: '5px 8px', fontSize: 12.5 }} />
                        </label>
                    ))}
                </div>
            )}
        </div>
    );
};
